import dayjs from "./dayjs";
import moment from "moment";
import { getRandomID } from "./common";
import { getTimeZones } from "@vvo/tzdb";
import { MODERATED_EVENT_STATUS, YESNO } from "../actions/constants";

// Format 24-hour time to 12-hour time like "10:00" → "10AM"
function formatTime(time) {
  const [hour, minute] = time.split(":").map(Number);
  const suffix = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return minute === 0 ? `${h}${suffix}` : `${h}:${minute.toString().padStart(2, "0")}${suffix}`;
}

// Convert slot objects to string like "10AM – 4PM"
function formatSlotRange(slot) {
  return `${formatTime(slot.start)} - ${formatTime(slot.end)}`;
}

// Check if two days' slots are exactly the same
function areSlotsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((s, i) => s.start === b[i].start && s.end === b[i].end);
}

// Main function
export function generateAvailability(schedule, eventStart, eventEnd) {
  const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weekly = schedule.weekly;
  const dateSpecific = {};

  // Expand date-specific overrides into a map
  (schedule.date_range || []).forEach(({ date_start, date_end, time_slots }) => {
    let current = dayjs(date_start);
    const end = dayjs(date_end);
    while (current.isSameOrBefore(end)) {
      dateSpecific[current.format("YYYY-MM-DD")] = time_slots;
      current = current.add(1, "day");
    }
  });

  const availableDays = [];
  let current = dayjs(eventStart);
  const end = dayjs(eventEnd);

  while (current.isSameOrBefore(end)) {
    const dateStr = current.format("YYYY-MM-DD");
    const dayOfWeek = current.format("dddd").toLowerCase();

    let slots = [];
    if (dateSpecific[dateStr]) {
      slots = dateSpecific[dateStr];
    } else if (weekly[dayOfWeek]?.is_available) {
      slots = weekly[dayOfWeek].slots || [];
    }

    if (slots.length > 0) {
      availableDays.push({
        date: current,
        day: dayMap[current.day()],
        slots,
      });
    }

    current = current.add(1, "day");
  }

  // Group consecutive days with the same slot pattern
  const result = [];
  for (let i = 0; i < availableDays.length; i++) {
    const groupStart = availableDays[i];
    let groupEnd = groupStart;
    let j = i + 1;

    while (
      j < availableDays.length &&
      areSlotsEqual(availableDays[j].slots, groupStart.slots) &&
      availableDays[j].date.diff(availableDays[j - 1].date, "day") === 1
    ) {
      groupEnd = availableDays[j];
      j++;
    }

    const title = `${groupStart.date.format("MMM D")}${
      groupStart.date.isSame(groupEnd.date, "day") ? "" : ` – ${groupEnd.date.format("D")}`
    }, ${groupEnd.date.format("YYYY")}`;
    const subtitle = `${groupStart.day}${groupStart.day !== groupEnd.day ? ` to ${groupEnd.day}` : ""}`;
    const slots = groupStart.slots.map(formatSlotRange);

    result.push({ title, subtitle, slots });

    i = j - 1; // skip ahead
  }

  return result;
}

// Convert minutes to time object
export function minutesToTimeObject(totalMinutes) {
  const duration = moment.duration(Number(totalMinutes), "minutes");
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  let timeString = "";

  // Special rule: if total minutes is a multiple of 60 with zero remainder
  if (minutes === 0) timeString = `${totalMinutes} minutes`;
  else {
    const parts = [];
    if (hours) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    if (minutes) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
    timeString = parts.join(" ");
  }

  return { hours, minutes, timeString };
}

/**
 * Converts provider's timezone slot to user's timezone.
 *
 * @param {Object} slot - { start: "HH:mm", end: "HH:mm" }
 * @param {string} selectedDate - Date string in YYYY-MM-DD
 * @param {string} providerTimezone - Provider timezone (default "Asia/Singapore")
 * @returns {Object} Converted slot with provider TZ time and metadata
 */
export function convertSlotToUserTimezone(slot, dateStr = "", providerTimezone = "Asia/Singapore", userTimezone = dayjs.tz.guess()) {
  const { start, end } = slot;

  // Default to Singapore timezone if not provided
  if (!providerTimezone) providerTimezone = "Asia/Singapore";

  const startInProviderTZ = dayjs.tz(`${dateStr} ${start}`, "YYYY-MM-DD HH:mm", providerTimezone);
  const endInProviderTZ = dayjs.tz(`${dateStr} ${end}`, "YYYY-MM-DD HH:mm", providerTimezone);

  const startInUserTZ = startInProviderTZ.tz(userTimezone);
  const endInUserTZ = endInProviderTZ.tz(userTimezone);

  return {
    slot,
    id: slot?.id || getRandomID(),
    startTimeObj: startInUserTZ,
    start: startInUserTZ.format("HH:mm"),
    end: endInUserTZ.format("HH:mm"),
    user_date: startInUserTZ.format("YYYY-MM-DD"),
    provider_time: `${start} - ${end}`,
    user_time: `${startInUserTZ.format("HH:mmA")} - ${endInUserTZ.format("HH:mmA")}`,
    userTimeInfo: `${startInUserTZ.format("hh:mmA")} - ${endInUserTZ.format("hh:mmA")}`,
  };
}

/**
 * Generates duration options in n-minute intervals.
 * @param {number} interval - Interval in minutes (e.g., 15).
 * @param {number} maxMinutes - Maximum duration in minutes (e.g., 240 for 4 hours).
 * @returns {Array<{ label: string, value: number }>}
 */
export function generateDurationOptions(interval = 15, maxMinutes = 240) {
  const steps = Math.floor(maxMinutes / interval);

  const formatLabel = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    // Special case: exactly 60 minutes
    if (minutes === 60) return "60 minutes";

    const parts = [];
    if (hrs) parts.push(`${hrs} hour${hrs > 1 ? "s" : ""}`);
    if (mins) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);

    return parts.join(" ");
  };

  return Array.from({ length: steps }, (_, i) => {
    const value = (i + 1) * interval;
    return { label: formatLabel(value), value };
  });
}

/**
 * Generates duration options in n-minute intervals.
 * @param {number} interval - Interval in minutes (e.g., 15).
 * @param {number} maxMinutes - Maximum duration in minutes (e.g., 240 for 4 hours).
 * @returns {Array<{ label: string, value: number }>}
 */
export function isAvailableOnDate(availability = null, dateToCheck = "", timezone = "Asia/Singapore") {
  if (!availability || !dateToCheck) return false;

  // Interpret the date to check in the provider's timezone
  const date = dayjs.tz(dateToCheck, timezone);
  if (!date.isValid()) return false;

  // 1. Check if date is within any date range (also in provider timezone)
  const inDateRange = availability.date_range?.some(({ date_start, date_end }) => {
    const start = dayjs.tz(date_start, timezone).startOf("day");
    const end = dayjs.tz(date_end, timezone).endOf("day");
    return date.isBetween(start, end, "day", "[]"); // inclusive
  });

  if (inDateRange) return true;

  // 2. Weekly config: get day name in provider's TZ
  const dayName = date.format("dddd").toLowerCase(); // e.g., "monday"
  const dayConfig = availability.weekly?.[dayName];

  return Boolean(
    dayConfig && Number(dayConfig?.is_available) === YESNO.YES && Array.isArray(dayConfig?.slots) && dayConfig.slots.length > 0
  );
}

// Get List of timezones
export function listTimezones() {
  const timezones = getTimeZones();

  return timezones?.map((tz) => {
    const offset = tz.currentTimeOffsetInMinutes;
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";

    const formattedOffset = `(GMT${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")})`;

    const cityName = tz.name.split("/").pop(); // e.g., "Kolkata" from "Asia/Kolkata"
    // replace underscores with spaces and capitalize
    const cityFormatted = cityName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      ...tz,
      value: tz.name, // e.g., "Asia/Kolkata"
      label_short: `${formattedOffset} ${cityFormatted}`, // e.g., "(GMT+05:30) IST"
      label: `${formattedOffset} ${tz.alternativeName} - ${cityFormatted}`, // e.g., "(GMT+05:30) India Standard Time - Kolkata"
    };
  });
}

// Get timezone by value
export function getTimezoneByValue(value) {
  const timezones = listTimezones();
  const obj = timezones?.find((tz) => tz?.value === value);
  return obj || timezones?.find((tz) => tz?.group?.includes(value));
}

// Get Available Slots for Date
export const getAvailableSlotsForEvent = (event = {}, availability = null, slotToKeep = null, userTimezone = dayjs.tz.guess()) => {
  const tz = event?.timezone || "Asia/Singapore";

  const duration = Number(event?.duration) || 60;
  const buffer = Number(event?.buffer_time) || 0;
  const noticeHours = Number(event?.notice_time) || 0;

  const now = dayjs().tz(tz);
  const noticeLimit = now.add(noticeHours, "hour");

  const startDate = dayjs.tz(event?.start_date, tz).startOf("day");
  const endDate = dayjs.tz(event?.end_date, tz).endOf("day");

  // user's availability
  const userAvailability = availability || event?.availability || {};

  const booked = new Set(
    event?.booked_slots
      ?.filter(
        ({ status, slot_date, slot_time }) =>
          status === MODERATED_EVENT_STATUS.BOOKED &&
          (!slotToKeep || slotToKeep.slot_date !== slot_date || slotToKeep.slot_time !== slot_time)
      )
      ?.map(({ slot_date, slot_time }) => `${slot_date} ${slot_time}`)
  );

  const results = [];

  for (let date = startDate; date.isSameOrBefore(endDate); date = date.add(1, "day")) {
    const dateStr = date.format("YYYY-MM-DD");
    const weekday = date.format("dddd").toLowerCase();

    const specificRange = userAvailability?.date_range?.find(({ date_start, date_end }) =>
      date?.isBetween(dayjs.tz(date_start, tz), dayjs.tz(date_end, tz), "day", "[]")
    );

    const daySlots =
      specificRange?.time_slots ||
      (Number(userAvailability?.weekly?.[weekday]?.is_available) === YESNO.YES ? userAvailability?.weekly[weekday].slots : []);

    daySlots?.forEach(({ start, end }) => {
      let slotStart = dayjs.tz(`${dateStr} ${start}`, "YYYY-MM-DD HH:mm", tz);
      const slotEnd = dayjs.tz(`${dateStr} ${end}`, "YYYY-MM-DD HH:mm", tz);

      while (slotStart.add(duration, "minute").isSameOrBefore(slotEnd)) {
        const slotTime = slotStart.format("HH:mm");
        const key = `${dateStr} ${slotTime}`;

        const isAvailable = slotStart.isAfter(noticeLimit) && !booked.has(key);

        // Available, push to results
        if (isAvailable) {
          results.push({ id: getRandomID(), date: dateStr, start: slotTime, end: slotStart.add(duration, "minute").format("HH:mm") });
        }

        slotStart = slotStart.add(duration + buffer, "minute");
      }
    });
  }

  // convert to user's timezone and filter out past slots
  const nowInUserTZ = dayjs().tz(userTimezone);

  return (
    results
      ?.map((slot) => convertSlotToUserTimezone(slot, slot?.date, tz, userTimezone))
      ?.filter((slot) => slot?.startTimeObj?.isAfter(nowInUserTZ)) || []
  );
};

// Convert slots to local date string in user's timezone
export const convertSlotsToLocalDate = (slots = [], userTimezone = dayjs.tz.guess()) => {
  return slots.map((slot) => {
    const slotTz = slot?.timezone || slot?.event?.timezone || "Etc/UTC";
    const dateTimeStr = slot?.start_time ? slot?.start_time : `${slot.slot_date} ${slot.slot_time}`;

    // Convert to user's timezone
    const dateInLocalTz = dayjs.tz(dateTimeStr, "YYYY-MM-DD HH:mm", slotTz).tz(userTimezone);

    return {
      ...slot,
      dateInLocalTz,
      dateStr: dateInLocalTz.format("YYYY-MM-DD"),
      dateStrTime: dateInLocalTz.format("YYYY-MM-DD HH:mm"),
    };
  });
};

// Format seconds to HH:MM:SS or MM:SS
export function formatSecondsToHhMmSs(seconds) {
  const pad = (num) => `0${num}`.slice(-2);

  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = pad(date.getUTCSeconds());

  return hh ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}
